import { CloudUploadIcon } from '@heroicons/react/solid'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import LogoutIcon from 'theorem-lib/src/assets/icons/logout.svg'
import TrashIcon from 'theorem-lib/src/assets/icons/trash.svg'
import { ImageCropper } from 'theorem-lib/src/components/atoms/ImageCropper/ImageCropper'
import TextInput from 'theorem-lib/src/components/atoms/Input/TextInput'
import { Modal } from 'theorem-lib/src/components/molecules/Modal/Modal'
import config from '../../../config'
import { User } from '../../../generated/api'
import { useActions, useAppState } from '../../../presenter'

export function UserEdit() {
  const { userEdit } = useAppState()
  const [confirmModal, setConfirmModal] = useState(false)
  const { submitUserEditAction, uploadAvatarImageAction, userRemoveAvatarAction } = useActions()
  const { formState: { errors, isDirty }, handleSubmit, register, reset } = useForm({
    defaultValues: userEdit,
  })
  const logoutURL = `${config.identityUrl}/logout`

  useEffect(() => {
    reset(userEdit)
  }, [userEdit])

  const handleSave = (payload: User) => {
    submitUserEditAction(payload)
  }

  const handleLogout = (e: React.MouseEvent) => {
    if (isDirty) {
      setConfirmModal(true)
    } else {
      window.location.href = logoutURL
    }
  }

  return (
    <div className='relative'>
      <h2 className='text-xl font-medium mb-5'>My Profile</h2>
      <div className='absolute top-0 right-0'>
        <a
          href={`${config.identityUrl}/settings`}
          className='bg-teal-400 text-white font-medium rounded py-2 px-4 text-sm mr-4 hover:text-white hover:no-underline'
        >
          Update Password
        </a>
        <button className='button text-black hover:text-black hover:no-underline' onClick={handleLogout}>
          <LogoutIcon className='mr-1 -mt-1 inline scale-75' />
          Log out
        </button>
      </div>
      <form
        onSubmit={handleSubmit(handleSave)}
      >
        <div>
          <h2 className='text-black mb-6 text-lg'>Photo</h2>
          <div
            className='inline-block h-20 w-20 align-middle rounded-full bg-primary-200 text-white text-3xl text-center leading-[80px] font-bold bg-center bg-cover'
            style={{ backgroundImage: `url(${userEdit.avatarUrl || ''})` }}
          >
            {!userEdit.avatarUrl && (
              <span>
                {userEdit.firstName?.charAt(0)}
                {userEdit.lastName?.charAt(0)}
              </span>
            )}
          </div>
          <div className='ml-8 inline-block align-middle'>
            <label htmlFor='cropper' className='cursor-pointer font-normal text-primary-200'>
              <CloudUploadIcon className='w-5 inline-block mr-1' />Upload new
            </label>
            {userEdit.avatarUrl && (
              <button
                type='button'
                className=' text-primary-300 mt-2'
                onClick={() => {
                  userRemoveAvatarAction()
                }}
              >
                <TrashIcon className='w-5 inline-block mr-1' />Remove Image
              </button>
            )}
          </div>
        </div>
        <ImageCropper
          size={300}
          setCroppedImageFile={(file) => uploadAvatarImageAction({ avatarFile: file, user: userEdit })}
        />
        <div className='mb-4 mt-10'>
          <h2 className='text-black mb-6 text-lg'>Personal Info</h2>
          <div className='inline-block w-2/5 align-top mr-4 h-16'>
            <TextInput
              label='First Name'
              type='text'
              {...register('firstName', { required: 'This field is required' })}
            />
            <div className='text-right text-error font-medium text-xs'>{errors.firstName?.message || ' '}</div>
          </div>
          <div className='inline-block w-2/5 align-top h-16'>
            <TextInput
              label='Last Name'
              type='text'
              {...register('lastName', { required: 'This field is required' })}
            />
            <div className='text-right text-error font-medium text-xs'>{errors.lastName?.message || ' '}</div>
          </div>
        </div>
        <div className='mb-4'>
          <h2 className='text-black mb-6 text-lg'>Contact Info</h2>
          <div className='inline-block w-2/5 align-top mr-4 h-16'>
            <TextInput
              label='Email'
              type='text'
              {...register('email', {
                pattern: {
                  message: 'Please enter a valid email address',
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                },
                required: 'This field is required.',
              })}
            />
            <div className='text-right text-error font-medium text-xs'>{errors.email?.message || ' '}</div>
          </div>
          <div className='inline-block w-2/5 align-top h-16'>
            <TextInput
              label='Phone Number (Optional)'
              type='tel'
              {...register('phone')}
            />
            <div className='text-right text-error font-medium text-xs'>{errors.phone?.message || ' '}</div>
          </div>
        </div>
        <button type='submit' className='button cta fixed right-4 bottom-4'>Save</button>
      </form>
      {confirmModal && (
        <Modal
          isOpen={confirmModal}
          onClose={() => setConfirmModal(false)}
        >
          <div className='flex flex-col justify-center items-center w-[80vw] sm:w-[558px]'>
            <div className='text-primary-100 text-xl font-semibold mb-2 text-center'>
              Are you sure you want to leave without saving?
            </div>
            <div className='text-primary-300 text-base font-normal text-center mb-6'>
              Changes you have made so far will not be saved.
            </div>
            <div className='flex flex-row'>
              <button type='button' className='button mx-2' onClick={() => setConfirmModal(false)}>Keep Editing</button>
              <button type='button' className='button cta mx-2' onClick={() => window.location.href = logoutURL}>
                Confirm
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
